import React from "react";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import Container from "@mui/material/Container";

import PanelContainerCard from "./cards/PanelContainerCard";

import i18n from "../i18n";
import { DatabaseContext } from "../database/DatabaseHandler";
import IParamScript from "../database/interfaces/paramScript/IParamScript";
import {CartContext} from "../cart/CartHandler";
import AddToCartContainer from "./AddToCartContainer";
import PrestaGlobals from "../common/PrestaGlobals";

/** Synthetic tab key that collects hidden params for admins only (not a DB `tab` value). */
const HIDDEN_TAB = "hidden";
const isHidden = (s: IParamScript): boolean => !!s.typeDescriptor?.hidden;

/**
 * A fülek MEGJELENÍTÉSI SORRENDJE — az EGYETLEN kódbeli fül-lista. A feliratok az i18n-ből jönnek
 * (`elements:tab.<kulcs>`), a fül pedig csak akkor jelenik meg, ha van hozzá LÁTHATÓ paraméter.
 *
 * Egy új fül bekötése tehát: egy elem ebbe a listába + egy kulcs a locales/hu/elements.json `tab`
 * blokkjába + az érték a DB `tab` enumjába (lásd V63). A komponensben nincs több teendő — korábban
 * ehhez a lista MELLETT egy `switch` ágat is bővíteni kellett, két helyen ugyanazt.
 *
 * A sorrend SZÁNDÉKOSAN itt van és nem a DB-ben: az enum csak az érvényes értékkészletet adja meg
 * (és a végén bővül, hogy a tárolt sorszámok ne csúszzanak el), a fülek egymáshoz mért rendje pedig
 * UI-döntés. A `tab` enumban létező, de itt fel nem sorolt értékek nem jelennek meg — erre a memo
 * figyelmeztet a konzolon.
 */
const TAB_ORDER = ["configuration", "drawer", "shelf", "materials", "railing"] as const;

/** Fül-felirat az i18n-ből; fordítás nélkül a nyers kulcs (így egy új fül azonnal használható). */
const tabLabel = (tab: string): string => i18n.t(`elements:tab.${tab}`, { defaultValue: tab });

type cardPlacementMapType = {
    [tab: string]: {
        [panel: string]: {
            [title: string]: IParamScript[];
        }
    }
}

interface ConfiguratorProps {
    isMobile: boolean;
}

const Configurator: React.FC<ConfiguratorProps> = ({ isMobile }) => {
    // Use the DatabaseContext to access global data
    const dataContext = React.useContext(DatabaseContext);

    const isAdmin = PrestaGlobals.isAdmin;

    const tabs: Record<string, string> = React.useMemo(() => {
        const scripts = dataContext.paramScripts;
        if (!scripts) return {};

        // A normal tab shows only if it has at least one VISIBLE param (hidden ones go to the Rejtett tab).
        const acc = TAB_ORDER
            .filter(tab => scripts.some(script => script.tab === tab && !isHidden(script)))
            .reduce((acc, tab) => {
                acc[tab] = tabLabel(tab);
                return acc;
            }, {} as Record<string, string>);

        // Diagnosztika: olyan `tab` érték az adatban, ami a TAB_ORDER-ben nem szerepel — az ilyen
        // paraméterek EGYETLEN fülön sem jelennek meg. Korábban ez nem derülhetett ki: a `switch`
        // `default:` ága elérhetetlen volt, mert maga a fül-lista is csak a három ismert kulcsot
        // tartalmazta. Márpedig a DB enumjában van nem használt érték ('construction', 'lábazat'),
        // és egy új fül (pl. 'drawer') migrációja is megelőzheti a hozzá tartozó frontend-buildet.
        const unknownTabs = Array.from(new Set(
            scripts.filter(script => !isHidden(script)).map(script => script.tab)
        )).filter(tab => !(TAB_ORDER as readonly string[]).includes(tab));
        if (unknownTabs.length) {
            console.warn(
                "[Configurator] Nem renderelt fül(ek) — van hozzájuk látható paraméter, de nincsenek " +
                `a TAB_ORDER-ben: ${unknownTabs.join(", ")}`
            );
        }

        // Admin-only last tab collecting all hidden params. Nem DB-s `tab` érték, de a felirata
        // ugyanabból az i18n blokkból jön, hogy minden fül-szöveg egy helyen legyen.
        if (isAdmin && scripts.some(isHidden)) {
            acc[HIDDEN_TAB] = tabLabel(HIDDEN_TAB);
        }
        return acc;
    }, [dataContext.paramScripts, isAdmin]);

    // Rebuild the card placement hierarchy map
    const cardPlacementMap: cardPlacementMapType = React.useMemo(() => {
        const map: cardPlacementMapType = {};
        if (!dataContext.paramScripts) return map;

        dataContext.paramScripts.forEach(script => {
            const hidden = isHidden(script);
            if (hidden && !isAdmin) return;          // customers never receive hidden params
            const tabKey = hidden ? HIDDEN_TAB : script.tab;   // hidden ones go to the Rejtett tab
            if (!map[tabKey]) {
                map[tabKey] = {};
            }
            const panelKey = script.panel || "";
            if (!map[tabKey][panelKey]) {
                map[tabKey][panelKey] = {};
            }
            if (!map[tabKey][panelKey][script.title]) {
                map[tabKey][panelKey][script.title] = [];
            }
            map[tabKey][panelKey][script.title].push(script);
        });
        return map;
    }, [dataContext.paramScripts, isAdmin]);

    const [currentTab, setCurrentTab] = React.useState(0);

    React.useEffect(() => {
        dataContext.paramScripts?.sort((a, b) => a.sortIndex - b.sortIndex);
        setCurrentTab(0);
    }, [dataContext.paramScripts]);

    // Calculate tabs height for dynamic content area sizing
    const tabsRef = React.useRef<HTMLDivElement>(null);
    const [tabsHeight, setTabsHeight] = React.useState(0);

    React.useEffect(() => {
        if (tabsRef.current) {
            setTabsHeight(tabsRef.current.offsetHeight);
        }
    }, [dataContext.paramScripts, currentTab]);

    return (
        <Container sx={{
            width: "100%",
            height: "100%",
            maxWidth: "100% !important",
            padding: "0px !important",
            display: "flex",
            flexDirection: "column"
        }}>
            {dataContext.paramScripts
                ?
                <Tabs
                    value={currentTab}
                    onChange={(event, newValue) => setCurrentTab(newValue)}
                    ref={tabsRef}
                >
                    {Object.keys(tabs).map((tabKey, idx) => {
                        return (
                            <Tab key={idx} label={tabs[tabKey]} />
                        );
                    })}
                </Tabs>
                :
                // Betöltés alatti placeholder: `value={false}` = "egyik fül sincs kiválasztva".
                // NEM adhatunk neki tab-értéket ("configuration"), mert az egyetlen <Tab>-nak nincs
                // `value` propja → MUI az indexét (0) használja, a "configuration" sosem talál rá, és
                // console error: "The `value` provided to the Tabs component is invalid.". A `false`
                // az egyetlen érték, amivel a MUI a keresést KIHAGYJA — a megjelenés változatlan
                // (eddig sem volt kiválasztott fül, sem indikátor a placeholderen).
                <Tabs value={false}>
                    <Tab label={tabLabel("configuration")} />
                </Tabs>
            }
            <Container sx={{ height: tabsHeight ? `calc(100% - ${tabsHeight}px)` : "100%", overflowY: "auto", maxWidth: "100% !important", padding: "0px !important" }}>
                {dataContext.paramScripts
                    ?
                    Object.keys(cardPlacementMap[tabs ? Object.keys(tabs)[currentTab] : ""] || {}).map(panelKey => {
                        const panelValue = cardPlacementMap[Object.keys(tabs)[currentTab]][panelKey];
                        return <PanelContainerCard
                            key={panelKey}
                            panel={panelKey}
                            titles={panelValue}
                        />;
                    })
                    : // Show a skeleton loader if no data is available yet
                    <Card sx={{ margin: 1, padding: 1 }}>
                        <Skeleton animation="wave">
                            <Typography variant="h6">
                                Név
                            </Typography>
                        </Skeleton>
                        <Skeleton animation="wave" width="100%">
                            <TextField fullWidth size="small" />
                        </Skeleton>
                    </Card>
                }
            </Container>

            <AddToCartContainer />
        </Container>
    );
}

export default Configurator;
